import { useCallback, useEffect, useMemo, useState } from 'react'
import { GraphQL } from '@guestgoodie/graphql'

import { QueryVariables } from './usePaginatedGoodies'
import debounce from 'lodash.debounce'

export default function useGoodieTable(
  possibleTypes: GraphQL.GoodieType[],
  initalSelectedGoodies: number[] = []
) {
  const [selectedGoodies, setSelectedGoodies] = useState<Set<number>>(
    new Set(initalSelectedGoodies)
  )

  const [search, setSearch] = useState<string>('')
  const [types, setTypes] = useState<string[]>([])

  const [sortDirection, setSortDirection] = useState<GraphQL.SortDirection>(
    GraphQL.SortDirection.Asc
  )

  const [sortField, setSortField] = useState<GraphQL.GoodiesSortField>(
    GraphQL.GoodiesSortField.Title
  )

  const updateSort = useCallback(
    (field: GraphQL.GoodiesSortField, direction: GraphQL.SortDirection) => {
      setSortField(field)
      setSortDirection(direction)
    },
    [setSortField, setSortDirection]
  )

  const filterTypes = useMemo(() => {
    return (types.length === 0 ? possibleTypes : types) as
      | GraphQL.GoodieType[]
      | null
  }, [types, possibleTypes])

  const [variables, setVariables] = useState<QueryVariables>(() => {
    return {
      filter: {
        types: filterTypes,
        search
      },
      sort: {
        direction: sortDirection,
        field: sortField
      }
    }
  })

  const onSelectGoodie = useCallback(
    (goodie: GraphQL.GoodieFragmentFragment) => {
      setSelectedGoodies(ids => {
        const newSet = new Set(ids)

        if (newSet.has(goodie.id)) newSet.delete(goodie.id)
        else newSet.add(goodie.id)

        return newSet
      })
    },
    [setSelectedGoodies]
  )

  const debounceSearch = useMemo(() => {
    return debounce(
      (search: string) =>
        setVariables(vars => ({
          ...vars,
          filter: {
            ...vars.filter,
            search
          }
        })),
      1000
    )
  }, [setVariables])

  useEffect(() => {
    if (variables.filter.search === search) return

    debounceSearch(search)
  }, [variables, search, debounceSearch])

  useEffect(() => {
    const currentTypes = variables.filter.types

    const hasSameStates =
      filterTypes === currentTypes ||
      (filterTypes?.length === currentTypes?.length &&
        filterTypes?.join(',') === currentTypes?.join(','))

    if (hasSameStates) return

    setVariables(variables => ({
      ...variables,
      filter: {
        ...variables.filter,
        types: filterTypes
      }
    }))
  }, [variables, filterTypes])

  useEffect(() => {
    if (
      variables.sort.direction === sortDirection &&
      variables.sort.field === sortField
    ) {
      return
    }

    setVariables(variables => ({
      ...variables,
      sort: {
        direction: sortDirection,
        field: sortField
      }
    }))
  }, [sortDirection, sortField, variables, setVariables])

  return {
    queryVariables: variables,
    selectedGoodies,
    search,
    types,
    onSelectGoodie,
    setTypes,
    setSearch,
    updateSort
  }
}
